
import { useToast } from "@/components/ui/use-toast";
import { usePublicClient, useWalletClient, useChainId } from "wagmi";
import { parseEther, type Hash, encodeDeployData } from "viem";
import { PLATFORM_FEE_COLLECTOR } from "../utils/contractConfig";
import { DISCOUNTED_PLATFORM_FEE, REFERRER_FEE } from "@/utils/referralSystem";
import { erc20Abi, bytecode } from "../utils/contractConfig";

interface FormData {
  name: string;
  symbol: string;
  initialSupply: string;
  description: string;
  referralCode?: string;
}

interface ReferralCode {
  referrerAddress: `0x${string}`;
}

export function useContractDeployment() {
  const { toast } = useToast();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  const chainId = useChainId();

  const deployContract = async (
    formData: FormData,
    address: `0x${string}`,
    referralCode: ReferralCode | null
  ): Promise<Hash> => {
    if (!walletClient) {
      toast({
        title: "Wallet Not Connected",
        description: "Please connect your wallet to create a coin",
        variant: "destructive",
      });
      throw new Error("Wallet client unavailable.");
    }

    try {
      // Optimize logging - only log in development environments
      if (import.meta.env.DEV) {
        console.log("Starting contract deployment with params:", { formData, address, referralCode });
      }

      // Calculate fees efficiently - do calculations outside of log statements
      const platformFee = referralCode 
        ? BigInt(Math.floor(DISCOUNTED_PLATFORM_FEE * 10000)) 
        : BigInt(2000);
      const referrerFee = referralCode 
        ? BigInt(Math.floor(REFERRER_FEE * 10000)) 
        : BigInt(0);
      
      // Parse initial supply at the beginning to fail fast
      let initialSupply: bigint;
      try {
        // Add validation before parsing to prevent unnecessary exceptions
        if (!formData.initialSupply || parseFloat(formData.initialSupply) <= 0) {
          throw new Error("Invalid supply amount");
        }
        initialSupply = parseEther(formData.initialSupply);
      } catch (error) {
        console.error("Error parsing initial supply:", error);
        toast({
          title: "Invalid Supply",
          description: "Please enter a valid positive number for initial supply",
          variant: "destructive",
        });
        throw new Error("Invalid initial supply format");
      }

      // Verify bytecode is valid early in the process
      if (!bytecode || bytecode.length <= 2) {
        console.error("Invalid bytecode");
        toast({
          title: "Contract Error",
          description: "The contract bytecode is invalid. Please contact support.",
          variant: "destructive",
        });
        throw new Error("Invalid contract bytecode");
      }

      // Get nonce - run this after validations to avoid unnecessary RPC calls
      const nonce = await publicClient.getTransactionCount({ address });
      
      // Prepare constructor arguments once, reuse the array
      const constructorArgs = [
        formData.name,
        formData.symbol,
        initialSupply,
        PLATFORM_FEE_COLLECTOR,
        referralCode?.referrerAddress || PLATFORM_FEE_COLLECTOR,
        platformFee,
        referrerFee,
      ] as const;
      
      // Prepare deployment data 
      const deployData = encodeDeployData({
        abi: erc20Abi,
        bytecode,
        args: constructorArgs,
      });

      // Gas optimization strategy
      const gasLimit = await estimateGasWithFallback(
        publicClient, 
        address, 
        deployData
      );
      
      // Get current gas price with optimized boost calculation
      const gasPrice = await publicClient.getGasPrice();
      // 10% boost (1.1x) for faster confirmation, using bit shift for efficiency
      const boostedGasPrice = (gasPrice * BigInt(110)) / BigInt(100);
      
      // Send transaction with optimized parameters
      toast({
        title: "Deploying Contract",
        description: "Please confirm the transaction in your wallet...",
      });
      
      const hash = await walletClient.sendTransaction({
        account: address,
        to: undefined,  // Use undefined for contract deployment
        data: deployData,
        gas: gasLimit,
        gasPrice: boostedGasPrice,
        chainId,
        nonce,  // Include nonce to prevent transaction collisions
      } as any);

      toast({
        title: "Transaction Sent",
        description: "Your token is being created. This may take a minute...",
      });

      // Handle receipt monitoring
      monitorTransaction(publicClient, hash, toast);

      return hash;
    } catch (error: any) {
      handleDeploymentError(error, toast);
      throw error;
    }
  };

  // Extracted function for better organization
  const estimateGasWithFallback = async (
    client: typeof publicClient,
    address: `0x${string}`,
    deployData: `0x${string}`
  ): Promise<bigint> => {
    try {
      const estimatedGas = await client.estimateGas({
        account: address,
        data: deployData,
      });
      // Add a 20% buffer for safety
      return (estimatedGas * BigInt(120)) / BigInt(100);
    } catch (error) {
      // Default to 4 million gas units as fallback with logging
      console.warn("Gas estimation failed, using fallback limit:", error);
      return BigInt(4000000);
    }
  };

  return { deployContract };
}

// Helper functions extracted to reduce the main function's complexity

async function monitorTransaction(
  publicClient: any, 
  hash: Hash, 
  toast: any
) {
  try {
    const receipt = await publicClient.waitForTransactionReceipt({ hash });
    
    if (receipt.status === 'success') {
      toast({
        title: "Contract Deployed Successfully",
        description: `Your token is ready! Contract address: ${receipt.contractAddress}`,
      });
    } else {
      toast({
        title: "Transaction Failed",
        description: "The contract creation transaction failed on the blockchain",
        variant: "destructive",
      });
    }
  } catch (error: any) {
    console.error("Error monitoring transaction:", error);
    toast({
      title: "Transaction Status Unknown",
      description: "Please check the transaction on the block explorer",
      variant: "destructive",
    });
  }
}

function handleDeploymentError(error: any, toast: any) {
  console.error("Contract deployment failed:", error);
  
  let errorMessage = "Unknown error occurred";
  if (error.message) {
    if (error.message.includes("insufficient funds")) {
      errorMessage = "You don't have enough MATIC to deploy the contract";
    } else if (error.message.includes("user rejected")) {
      errorMessage = "You rejected the transaction";
    } else if (error.message.includes("gas required exceeds")) {
      errorMessage = "The contract deployment is too expensive";
    } else if (error.message.includes("nonce too low")) {
      errorMessage = "Transaction nonce error. Please try again";
    } else {
      errorMessage = error.message;
    }
  }

  toast({
    title: "Deployment Failed",
    description: errorMessage,
    variant: "destructive",
  });
}
